<template>
  <div class="container-fluid">
    <div class="card card-default custom-card-height">
      <div class="card-header">
        <div class="row g-2">
          <div class="col-auto me-auto">
            <CardTitleComponent />
          </div>
          <div class="col-auto">
            <SearchComponent v-model="searchText" @search="onSearch" @clear="clearSearch" />
          </div>
          <div class="col-auto">
            <a class="btn btn-outline-primary btn-sm" @click="open()">
              <i class="fas fa-plus"></i>&nbsp; {{ $t("addNew") }}
            </a>

          </div>
        </div>
      </div>
      <div class="card-body card-body-custom-layout">

        <!-- Legacy Auth Info Bar -->
        <div v-if="hasLegacyCompanies" class="legacy-info-bar mx-3 mt-2 mb-1 p-3 d-flex align-items-start gap-2"
          role="alert">
          <i class="fas fa-info-circle legacy-info-icon mt-1 flex-shrink-0"></i>
          <div>
            <strong>Authentication Update Required</strong>
            <p class="mb-1 mt-1" style="font-size: 0.85rem;">
              We're improving the way Fortnox data is synchronized by introducing a more convenient and secure
              authentication method.
              As part of this update, we're moving away from the previous token-based login.
            </p>
            <p class="mb-1" style="font-size: 0.85rem;">
              To continue using the integration, you'll need to <strong>re-authorize your Fortnox connection</strong>
              and generate new tokens. If you need any help with the process, feel free to reach out.
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div v-if="alert.show"
              class="alert alert-warning alert-dismissible fade show mb-0 flex-grow-1 ms-2 mb-2 fortnox-company-alert"
              role="alert" style="font-size: small; padding: 0.5rem 0rem 0rem 0rem; margin-right: 10px;">
              <ul>
                <li v-for="(msg, index) in alert.messages" :key="index">{{ msg }}</li>
              </ul>
              <button type="button" class="close" data-bs-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        </div>
        <div class="fortnox-card-body-grid" :class="hasLegacyCompanies? 'fortnox-company-with-legacy' : 'fortnox-company-no-legacy'">

          <ejs-grid ref="gridCompany" :dataSource="data" :allowPaging="true" :allowResizing="true"
            :pageSettings="pageSettings" :locale="locale" :rowHeight="30" :allowSorting="true"
            :searchSettings="searchOptions" :dataStateChange="dataStateChange" :commandClick="commandClick"
            :height="'100%'" :width="'100%'" :allowScrolling="true">
            <e-columns>

              <e-column :headerText="$t('legalEntity')" field="legalEntityName" width="200"></e-column>
              <e-column :headerText="$t('name')" field="Name" width="200"></e-column>
              <e-column :headerText="$t('fortnoxName')" field="fortnoxName" width="200"></e-column>
              <e-column :headerText="$t('creationTime')" field="CreationTime" type="date" format="yyyy-MM-dd hh:mm a"
                :template="'creationTimeTemplate'">
                <template v-slot:creationTimeTemplate="{ data }">
                  <span>
                    {{ getRelativeSwedishDatetime(data.creationTime) }}
                  </span>
                </template>
              </e-column>
              <e-column :headerText="$t('refreshTokenExpiryDate')" field="RefreshTokenExpireDate" type="date"
                format="yyyy-MM-dd hh:mm a" :template="'refreshTokenExpireDateTemplate'">
                <template v-slot:refreshTokenExpireDateTemplate="{ data }">
                  <span
                    :class="`expire-date badge ${data.isRefreshTokenExpire ? 'text-bg-danger' : 'text-bg-success'}`">
                    {{ getRelativeSwedishDatetime(data.refreshTokenExpireDate) }}
                  </span>
                </template>
              </e-column>
              <e-column :headerText="$t('lastSynced')" field="LastSyncDate" type="date" format="yyyy-MM-dd hh:mm a"
                :template="'lastSyncedDateTemplate'">
                <template v-slot:lastSyncedDateTemplate="{ data }">
                  <span>
                    {{ getRelativeSwedishDatetime(data.lastSyncDate) }}
                  </span>
                </template>
              </e-column>
              <e-column :headerText="$t('syncEnabled')" field="DoSync" type="boolean" :template="'doSyncTemplate'"
                text-align="Center">
                <template v-slot:doSyncTemplate="{ data }">
                  <span>
                    <i class="fas fa-check text-green" v-if="data && data.doSync"></i>
                    <i class="fas fa-times-circle text-gray" v-else></i>
                  </span>
                </template>
              </e-column>
              <e-column :headerText="$t('scopeChangeApplied')" field='isScopeChangeApplied' type="boolean"
                :template="'scopeChangeTemplate'" text-align="Center">
                <template v-slot:scopeChangeTemplate="{ data }">
                  <span>
                    <i class="fas fa-check text-green" v-if="data && data.isScopeChangeApplied"></i>
                    <i class="fas fa-times-circle text-yellow" v-else></i>
                  </span>
                </template>
              </e-column>
              <e-column :headerText="$t('actions')" width="300" textAlign="Center" freeze="Right"
                :template="'commandsTemplate'">
                <template v-slot:commandsTemplate="{ data }">
                  <div class="btn-group actions-row d-inline-flex align-items-center actions-row gap-2" role="group"
                    aria-label="Row actions">
                    <a href="javascript:void(0)" class=" action-link action-edit" title="Edit"
                      @click="onEditButtonClick(data)">
                      <i class="e-edit e-icons"></i>
                    </a>
                    <a href="javascript:void(0)" class=" action-link action-delete" title="Delete"
                      @click="onDeleteButtonClick(data)">
                      <i class="e-delete e-icons" style="font-size: 0.76em;"></i>
                    </a>
                    <a href="javascript:void(0)" class=" action-link action-add" title="Force Fortnox Sync"
                      @click="onForceSyncButtonClick(data)">
                      <i class="e-repeat e-icons"></i>
                    </a>
                    <button
                      :class="(data.isRefreshTokenExpire || data.isTokenMissing) ? 'e-danger' : !data.isScopeChangeApplied ? 'e-warning' : 'e-success'"
                      class="e-btn e-small" title="Authenticate Fortnox" @click="onActivateFortnoxButtonClick(data)">
                      Authenticate
                    </button>
                  </div>
                </template>
              </e-column>
            </e-columns>
          </ejs-grid>
        </div>
      </div>
    </div>
    <!-- /.card-body -->
  </div>

  <div class="modal fade" id="companyModal" role="dialog">
    <div class="modal-dialog modal-lg">
      <div class="modal-content" :key="formKey">
        <div class="modal-header">
          <h4 class="modal-title">{{ $t("company") }}</h4>
          <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close" @click="close()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <form @submit="onUpsert">
          <div class="modal-body">
            <div class="row" v-if="!isUpdate">
              <div class="col-6">
                <div class="form-group">
                  <div class="icheck-primary">
                    <input type="checkbox" class="form-check-input me-1" id="createLegalEntity"
                      v-model="form.createLegalEntity" />
                    <label for="createLegalEntity">
                      Create Legal Entity
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col" v-if="!form.createLegalEntity">
                <div class="form-group">
                  <label>{{ $t("legalEntity") }} <span class="text-danger">*</span></label>
                  <ejs-dropdownlist :enabled="!isUpdate" id="legalEntityDropDown" ref="legalEntityDropDown"
                    :dataSource="legalEntities" :fields="{ text: 'name', value: 'id' }" v-model="form.legalEntityId"
                    :placeholder="$t('legalEntity')" popupWidth="auto" :showClearButton="true">
                  </ejs-dropdownlist>
                </div>
              </div>
              <div class="col">
                <div class="form-group">
                  <label>{{ $t("name") }} <span class="text-danger">*</span></label>
                  <!-- <input type="text" class="e-input" v-model="form.name" required /> -->
                  <ejs-textbox v-model="form.name" :placeholder="$t('companyName')" type="text"
                    id="companyName"></ejs-textbox>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <div class="icheck-success">
                    <input type="checkbox" class="form-check-input me-1" id="doSync" v-model="form.doSync" />
                    <label for="doSync">
                      Enable Syncing
                    </label>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">

                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="col-12">
                <label>{{ $t('scopes') }} <span class="text-danger">*</span></label>
              </div>
              <div class="col-12 tree-view-container">
                <CustomTreeView ref="treeView" @node-check="nodeChecking" :dataSource="treeViewData"
                  v-model="scopeTreeSelectedNodes" :disabledNodes="disabledNodes" />
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-close" @click="close()">
              <i class="fas fa-times"></i>&nbsp; {{ $t("close") }}
            </button>

            <button type="submit" class="btn btn-sm btn-outline-info-reverse">
              <i class="fas fa-save"></i>&nbsp;
              {{ isUpdate ? $t("update") : $t("save") }}
            </button>
          </div>
        </form>
      </div>
      <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
  </div>
  <!-- Force sync modal -->
  <div class="modal fade" id="force-sync-modal" role="dialog">
    <div class="modal-dialog modal-xs">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Fortnox sync</h4>
          <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close" @click="closeForceSyncModal">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-md-12">
              <div class="mb-2">
                <!-- <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="forceSync.type" value="full"
                    name="flexRadioDefault" id="flexRadioDefault1" />
                  <label class="form-check-label" for="flexRadioDefault1">
                    Full Sync
                  </label>
                </div> -->
                <div class="col">
                  <div class="form-group">
                    <ejs-radiobutton id="flexRadioDefault1" value="full" label="Full Sync" name="forceSyncType"
                      @change="onForceSyncTypeChange('full')" :checked="forceSync.type === 'full'" />
                  </div>
                </div>
              </div>
              <div class="mb-2">
                <!-- <div class="form-check">
                  <input class="form-check-input" type="radio" v-model="forceSync.type" value="custom"
                    name="flexRadioDefault" id="flexRadioDefault2" />
                  <label class="form-check-label" for="flexRadioDefault2">
                    Custom Sync
                  </label>
                </div> -->
                <div class="col">
                  <div class="form-group">
                    <ejs-radiobutton id="flexRadioDefault2" value="custom" label="Custom Sync" name="forceSyncType"
                      @change="onForceSyncTypeChange('custom')" :checked="forceSync.type === 'custom'" />
                  </div>
                </div>
              </div>

              <div class="mb-2" v-show="forceSync.type == 'custom'">
                <div class="col">
                  <div class="form-group">
                    <label>Date: <span class="text-danger">*</span></label>
                    <DatePickerComponent v-model="forceSync.date" format="yyyy-MM-dd" :max="today"
                      placeholder="Select a date to sync from" />
                  </div>
                </div>
              </div>
              <div class="mb-2">
                <div class="col">
                  <div class="form-group">
                    <ejs-checkbox id="isSpecificEntities" v-model="forceSync.isSpecificEntities"
                      label="Specific Entities"></ejs-checkbox>
                  </div>
                </div>
              </div>
              <div class="mb-2" v-show="forceSync.isSpecificEntities">
                <div class="col">
                  <div class="form-group">
                    <label for="entities" class="form-label">{{ $t('entities') }} <span
                        class="text-danger">*</span></label>
                    <ejs-multiselect id="entities" v-model="forceSync.entities" :dataSource="entityLookUp.data"
                      :fields="entityLookUp.fields" :placeholder="$t('selectEntity')" popupWidth="auto" mode="CheckBox"
                      :showClearButton="false" :showSelectAll="true">
                    </ejs-multiselect>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="closeForceSyncModal">
            <i class="fas fa-times"></i>&nbsp; {{ $t("close") }}
          </button>

          <button v-on:click="forceSyncByDate()" :disabled="forceSync.type == 'custom' && !forceSync.date" type="button"
            class="btn btn-sm btn-outline-info">
            <i class="fas fa-save"></i>&nbsp; Request Sync
          </button>
        </div>
      </div>
      <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
  </div>
</template>

<script lang="ts" src="./Company.service">
import './CompanyComponent.css';
import en from '@/locales/en.js';

</script>
